import * as React from 'react';
import { jsx, css, keyframes } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { FeaturePropsType } from '../../../../../chat/views/types';
import type { RenderCheckboxGroupPropsType } from '../../../../../chat/views/CheckboxGroup/index';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { CheckboxButton } from './components/CheckboxButton';

const { tablet, mobile } = mediaQueries();

export type CheckboxGroupPropsType = FeaturePropsType<RenderCheckboxGroupPropsType>

export const CheckboxGroup = (props: CheckboxGroupPropsType) => {
  const {
    keyName,
    isInstant,
    doesNotScroll,
    enableHideAnimation: enableHideAnimationFromProps = true,
    shouldEnableHideAnimation,
    continueHandler,
    answers,
    applyHandler,
    skipHandler,
  } = props;
  const [isAnswered, setIsAnswered] = React.useState(false);
  const [showAnswerPanel, setShowAnswerPanel] = React.useState(true);
  const [isShow, setIsShow] = React.useState(isInstant);
  const [enableHideAnimation, setEnableHideAnimation] = React.useState(enableHideAnimationFromProps);
  const [checkedAnswerIds, setCheckedAnswerIds] = React.useState<number[]>(answers.map((answer) => answer.checked && answer.id).filter((id) => id));

  const onApplyButtonClickHandler = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    // 回答済みフラグを立てる
    setIsAnswered(true);

    // 回答内容を作成
    const checkedAnswers = checkedAnswerIds.map((id) => answers.find((answer) => answer.id === id)).filter((item) => item);
    const answerText = checkedAnswers.map((answer) => answer.textContents).join(', ');
    const valueText = checkedAnswers.map((answer) => answer.value).join(', ');

    // 回答時の処理を実行
    applyHandler(checkedAnswers);

    // 回答内容を渡して次の処理に進む
    continueHandler({
      value: valueText,
      answerText,
      selfDomReferences: [],
    });
  }, [checkedAnswerIds, answers, applyHandler, continueHandler]);

  const onSkipButtonClickHandler = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    // 回答済みフラグを立てる
    setIsAnswered(true);

    // 回答時の処理を実行
    skipHandler();

    // 回答内容を渡して次の処理に進む
    continueHandler({
      value: '未選択',
      answerText: '選択しないで次へ',
      selfDomReferences: [],
    });
  }, [skipHandler, continueHandler]);

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(0.4);

        setIsShow(true);
      }
    })();
  }, [isInstant]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        transition: grid-template-rows ${setTime(0.4)}s;

        ${!showAnswerPanel && css`
          overflow: hidden;
          grid-template-rows: minmax(0, 0fr);

          ${!enableHideAnimation && css`
            display: none;
          `}
        `}
      `}
    >
      <div
        css={css`
          padding: 30px 12px;
          position: relative;
          opacity: 0;
          bottom: -10px;
          transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

          ${isShow && css`
            opacity: 1;
            bottom: 0;
          `}
        `}
      >
        <div
          css={css`
            background-color: #bae8e8;
            border-radius: 8px;
            padding: 40px 40px 20px;

            ${mobile(() => css`
              background-color: transparent;
              padding: 0;
            `)}
          `}
        >
          <div
            css={css`
              margin-bottom: 20px;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                background-color: #bae8e8;
                border-radius: 8px;
                margin-bottom: 30px;
                padding: ${setAutomaticDesignBasedSize(40, 20)} ${setAutomaticDesignBasedSize(40, 15)} ${setAutomaticDesignBasedSize(30, 30)};
              `)}
            `}
          >
            <div
              css={css`
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 10px 20px;
                margin-bottom: 40px;

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  gap: ${setAutomaticDesignBasedSize(10, 10)} ${setAutomaticDesignBasedSize(20, 10)};
                  margin-bottom: ${setAutomaticDesignBasedSize(40, 20)};
                `)}
              `}
            >
              {answers.map((answer) => {
                const { id } = answer;

                return (
                  <CheckboxButton
                    key={id}
                    {...props}
                    {...answer}
                    isAnswered={isAnswered}
                    setCheckedAnswerIds={setCheckedAnswerIds}
                  />
                );
              })}
            </div>
            <div
            >
              <button
                type="button"
                id={`ga_event_${keyName}_next`}
                disabled={isAnswered || checkedAnswerIds.length === 0}
                onClick={onApplyButtonClickHandler}
                css={css`
                  color: #041156;
                  font-size: 26px;
                  font-weight: bold;
                  text-align: center;
                  max-width: 210px;
                  margin-inline: auto;
                  padding: 18px 0;
                  background-color: #fff;
                  border: 2px solid #041156;
                  border-radius: 10px;
                  box-shadow: 0 4px 0 #545971;

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    font-size: ${setAutomaticDesignBasedSize(26, 18)};
                    max-width: ${setAutomaticDesignBasedSize(210, 140)};
                    padding: ${setAutomaticDesignBasedSize(18, 13)} 0;
                  `)}

                  &:hover {
                    background-color: #fff7c9;
                  }

                  &:disabled {
                    color: #bfbfbf;
                    background-color: #e1e1e1;
                    border-color: #bfbfbf;
                    box-shadow: 0 2px 0 #cecece;
                    pointer-events: none;
                  }
                `}
              >
                次へ
              </button>
            </div>
          </div>
          <button
            type="button"
            id={`ga_event_${keyName}_skip`}
            onClick={onSkipButtonClickHandler}
            css={css`
              color: #8e8e8e;
              font-size: 16px;
              text-align: center;
              width: fit-content;
              margin-inline: auto;
              padding-left: 24px;
              position: relative;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(16, 16)};
              `)}

              &::before,
              &::after {
                content: "";
                border-right: 2px solid #8e8e8e;
                border-top: 2px solid #8e8e8e;
                width: 8px;
                height: 8px;
                position: absolute;
                top: 47%;
                transform: translateY(-50%) rotate(45deg);
              }

              &::before {
                left: 0;
              }

              &::after {
                left: 6px;
              }

              ${isAnswered && css`
                pointer-events: none;
              `}
            `}
          >
            選択しないで次へ
          </button>
        </div>
      </div>
    </div>
  );
};
